import { useState, useCallback } from 'react';

interface UsePaginationOptions {
  defaultPage?: number;
  defaultLimit?: number;
}

interface UsePaginationReturn {
  page: number;
  limit: number;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  resetPagination: () => void;
}

/**
 * Reusable hook for managing pagination state
 *
 * @example
 * const { page, limit, setPage } = usePagination({ defaultPage: 1, defaultLimit: 25 });
 * const { data } = useQuery({ page, limit });
 */
export function usePagination({
  defaultPage = 1,
  defaultLimit = 25,
}: UsePaginationOptions = {}): UsePaginationReturn {
  const [page, setPage] = useState(defaultPage);
  const [limit, setLimit] = useState(defaultLimit);

  const resetPagination = useCallback(() => {
    setPage(defaultPage);
    setLimit(defaultLimit);
  }, [defaultPage, defaultLimit]);

  return {
    page,
    limit,
    setPage,
    setLimit,
    resetPagination,
  };
}
